import { Inject, Injectable, isDevMode, DOCUMENT } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  Observable,
  filter,
  fromEvent,
  interval,
  merge,
  throttleTime,
} from 'rxjs';
import { buildInfo } from 'src/environments/build-info';

const VERSION_URL = 'assets/version.json';
const CHECK_INTERVAL_MS = 15 * 60 * 1000;
const NAVIGATION_THROTTLE_MS = 5 * 60 * 1000;
// Pendant un déploiement, version.json peut être à jour avant les bundles :
// on ne signale la nouvelle version qu'après une seconde vérification.
const CONFIRMATION_DELAY_MS = 60 * 1000;

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class AppVersionService {
  readonly current = buildInfo;

  private readonly _updateAvailable = new BehaviorSubject<boolean>(false);
  readonly updateAvailable$: Observable<boolean> =
    this._updateAvailable.asObservable();

  private started = false;
  private checking = false;

  constructor(
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  start(): void {
    // En dev, build-info est régénéré à chaque ng serve : pas de détection.
    if (this.started || isDevMode()) return;
    this.started = true;

    const isVisible = () => this.document.visibilityState === 'visible';

    merge(
      fromEvent(this.document, 'visibilitychange').pipe(filter(isVisible)),
      interval(CHECK_INTERVAL_MS).pipe(filter(isVisible)),
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        throttleTime(NAVIGATION_THROTTLE_MS),
      ),
    )
      .pipe(untilDestroyed(this))
      .subscribe(() => this.check());

    // Nouvelle version connue : la navigation suivante se termine par un rechargement.
    // Au NavigationEnd, les guards (dont HasUnsavedChangesGuard) sont passés, l'ancien
    // composant est détruit (son beforeunload ne se déclenche plus) et l'URL est à jour :
    // reload() recharge la page cible sans doublon d'historique, history.state conservé.
    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd && this._updateAvailable.value,
        ),
        untilDestroyed(this),
      )
      .subscribe(() => this.reload());
  }

  reload(): void {
    this.document.location.reload();
  }

  private async check(): Promise<void> {
    // Un seul cycle vérification + confirmation à la fois, même si plusieurs
    // déclencheurs arrivent ensemble (retour sur l'onglet + navigation).
    if (this._updateAvailable.value || this.checking) return;
    this.checking = true;

    try {
      if (!(await this.isOutdated())) return;
      await new Promise((resolve) => setTimeout(resolve, CONFIRMATION_DELAY_MS));
      if (await this.isOutdated()) this._updateAvailable.next(true);
    } finally {
      this.checking = false;
    }
  }

  private async isOutdated(): Promise<boolean> {
    try {
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        cache: 'no-store',
      });
      if (!response.ok) return false;

      const online: { buildId?: string } = await response.json();
      return !!online.buildId && online.buildId !== this.current.buildId;
    } catch {
      // Réseau coupé, 404 pendant un déploiement… : on retentera au prochain déclencheur.
      return false;
    }
  }
}
